/**
 * Auth domain types — SmartDash dual login (ADR-003 D8).
 *
 * Two sessions are accepted, each with a distinct surface:
 *   - customer  → /api/auth/{login,me,logout} on the per-host gateway (HTTP-only
 *     SMARTDASH_SESSION cookie; JS-readable SMARTDASH_CSRF for the double-submit)
 *   - employee  → /api/staff-auth/{login,verify,me,logout} on the BFF (HTTP-only
 *     SIHSESSION cookie; CSRF cookie-repo for state-changing POSTs)
 *
 * The two surfaces are interchangeable for route-guard purposes (either session
 * unlocks the dashboard). The AppHeader renders a kind badge so the user can
 * see which session is active; sign-out routes to the right surface.
 *
 * LAYER LAW: this is the innermost layer. It imports nothing from the app.
 */

import { ApiError } from './types'

/**
 * Which auth surface owns the current session.
 * - `local`    → customer user authenticated via the per-host gateway.
 * - `employee` → chrilantech staff authenticated via the TicketApp SSO BFF.
 */
export type AuthKind = 'local' | 'employee'

/** Display profile for the authenticated session (no password hash, no token). */
export interface AuthUser {
  readonly id: number
  /**
   * Present for customer (gateway) sessions only. The staff BFF surface
   * redacts the TicketApp Username by design (ADR-001) — staff sessions
   * carry displayName + role (officeName) only.
   */
  readonly username?: string
  readonly displayName: string
  readonly role: string
}

/** Coarse-grained view of the auth state for route guards and the login page. */
export type AuthStatus =
  /** Initial mount — never probed either auth surface yet. */
  | 'unknown'
  /** AuthStore.hydrate() is in flight (calls GET on the appropriate /me). */
  | 'hydrating'
  /** Both surfaces 401 — show the login page. */
  | 'anonymous'
  /** Either /me returned 200 — dashboard accessible. */
  | 'authenticated'
  /** Login attempt in flight. `authenticatingForKind` records which surface. */
  | 'authenticating'
  /**
   * Employee flow step 1 succeeded (BFF returned 202 with the OTP-prompt
   * loginRef); the SPA is now waiting on the user to enter the OTP. No network
   * call is in flight — this is distinct from `authenticating` so the LoginPage
   * can re-enable the OTP input (and the OTP input was the bug: the old
   * implementation never left `authenticating`, leaving the input permanently
   * disabled and the e2e `login-otp` step timing out).
   */
  | 'awaiting_otp'
  /** Login attempt rejected — show the named error under the form. */
  | 'failed'

/** Result of /api/auth/login (customer) on the 2xx path. */
export interface CustomerLoginResult {
  readonly sessionId: string
  readonly csrfToken: string
  readonly user: AuthUser
}

/**
 * Result of /api/staff-auth/login (employee) on the 2xx path — the BFF responds
 * 202 with an opaque loginRef + the OTP-prompt message. The SPA then POSTs
 * /api/staff-auth/verify with the OTP to establish the SIHSESSION cookie.
 *
 * After verify() succeeds the SPA holds the same AuthUser shape as the
 * customer flow — the two surfaces are interchangeable for the route guard.
 */
export interface EmployeeLoginResult {
  readonly loginRef: string
  readonly displayName: string
  readonly message: string
  /** Present only after a successful verify — empty until then. */
  readonly user?: AuthUser
}

/** Result of GET /api/{auth,staff-auth}/me on the 2xx path. */
export interface MeResult {
  readonly user: AuthUser
}

/**
 * Mapped login failure surfaced by authStore.login(). Throws an ApiError-shaped
 * value so the caller can render the named state without re-classifying.
 */
export class AuthApiError extends ApiError {
  constructor(
    status: number,
    code: string,
    message: string,
    /** Hint for the login UI; maps to the user-facing copy per the task contract. */
    public readonly reason: LoginFailureReason,
  ) {
    super(status, code, message)
    this.name = 'AuthApiError'
  }
}

/**
 * Pre-classified reason — keeps the LoginPage a dumb renderer that maps
 * reason → user-facing message (per ADR-003 §1.2 QAS-2; mapping is exhaustive
 * over the gateway contract codes).
 */
export type LoginFailureReason =
  /** 400 — gateway/BFF rejected blank/malformed credentials. */
  | 'validation'
  /** 401 at the login step — wrong username/password, unknown user, or a refused (archived) account: one generic message. */
  | 'invalidCredentials'
  /** 423 — account is locked (5 failed attempts in 60s within 15 min). */
  | 'locked'
  /** 429 — too many requests, see Retry-After. */
  | 'rateLimited'
  /** 503 and network failure — the sign-in service cannot be reached. Nothing else. */
  | 'unavailable'
  /** Verify step, `OTP_VERIFICATION_FAILED` — the code was invalid or expired (ADR-001 4.5). Never the credential message. */
  | 'otpFailed'
  /** Verify step, `AUTHENTICATION_FAILED` — the sign-in attempt expired or the account was refused (the contract does not say which). */
  | 'verifyAuthFailed'
  /** Any (status, code) pair the contract does not name — a real, named state, not "unavailable" (ADR-001 O2). */
  | 'authFailureGeneric'
  /** 429 `PENDING_LOGIN_LIMIT` — too many sign-ins are in progress at once; same title as `rateLimited`, its own sentence. */
  | 'pendingLoginLimit'

/** The reasons only the verification-code step (or an unnamed pair) can produce; the page holds their copy apart. */
export type StepReason = 'otpFailed' | 'verifyAuthFailed' | 'authFailureGeneric' | 'pendingLoginLimit'

/** One failure state's visible copy: a short title and the sentence under it. */
export interface StepReasonCopy {
  readonly title: string
  readonly detail: string
}

/**
 * The copy of the verification-step states and of the 429 for too many sign-ins in progress (PM
 * disposition PD-37 N8(b), final). A state's visible text is its title and its detail joined by one
 * space, and that joined text is the exact approved string; where the approved string is one message
 * the title is its first sentence with the full stop. Each is true for every cause its state covers,
 * and none says "Service unavailable", which is reserved for 503 and an unreachable service.
 */
export const STEP_REASON_COPY: Readonly<Record<StepReason, StepReasonCopy>> = {
  otpFailed: {
    title: 'That code did not work.',
    detail: 'Start sign-in again.',
  },
  verifyAuthFailed: {
    title: 'We could not sign you in.',
    detail: 'Start again, or contact the team that manages your staff login if this keeps happening.',
  },
  authFailureGeneric: {
    title: 'Sign-in failed.',
    detail: 'Try again, or contact the team that manages your staff login if this keeps happening.',
  },
  pendingLoginLimit: {
    title: 'Too many attempts',
    detail: 'Too many sign-in attempts are in progress. Wait a few minutes, then try again.',
  },
}

/**
 * The credential-failure copy at the login step (PM disposition, final). The state keeps its key,
 * `invalidCredentials`, and it also covers an archived account: the BFF answers a refused account
 * with the same code as a bad password, so the sentence has to be true for both.
 */
export const CREDENTIAL_FAILED_COPY: StepReasonCopy = {
  title: 'We could not sign you in.',
  detail: 'Check your username and password, then try again, or contact the team that manages your staff login if this keeps happening.',
}

/** True for the reasons whose copy lives in STEP_REASON_COPY; narrows the rest to the page's own table. */
export function isStepReason(reason: LoginFailureReason): reason is StepReason {
  return reason === 'otpFailed' || reason === 'verifyAuthFailed' || reason === 'authFailureGeneric' || reason === 'pendingLoginLimit'
}

/** The step the SPA called. The SPA owns this fact, so it can scope a code without a new server signal. */
export type AuthStep = 'credentials' | 'verify'

/** The BFF's code for an invalid or expired one-time code (OtpVerificationFailedException). */
export const OTP_REJECTED_CODE = 'OTP_VERIFICATION_FAILED'
/** The BFF's code for a refused sign-in: bad credentials at step 1, an expired attempt or refused account at step 2. */
export const AUTH_REJECTED_CODE = 'AUTHENTICATION_FAILED'
/** The BFF's 429 code when too many sign-ins are pending at once (PendingLoginCapacityException). */
export const PENDING_LIMIT_CODE = 'PENDING_LOGIN_LIMIT'

/**
 * Map an auth failure to the login page reason, from the (status, code) pair and the step
 * the SPA called (TL ruling 2026-10-31; ADR-001 4.5 and O2).
 *
 * - 400, 423, 429 and 503 keep their own states at either step.
 * - At the credentials step every 401 is a credential rejection.
 * - At the verify step a 401 is read by its code: `OTP_VERIFICATION_FAILED` is the code that
 *   did not work; `AUTHENTICATION_FAILED` is its own step-scoped state (it must not read as a
 *   password problem, and the contract does not separate an expired attempt from a refused
 *   account); any other 401 code is unknown.
 * - Every pair that is not named above is the generic named failure, never "unavailable".
 */
export function mapAuthErrorCode(status: number, code: string, step: AuthStep = 'credentials'): LoginFailureReason {
  if (status === 400) return 'validation'
  if (status === 423) return 'locked'
  if (status === 429) return code === PENDING_LIMIT_CODE ? 'pendingLoginLimit' : 'rateLimited'
  if (status === 503 || status === 0) return 'unavailable'
  if (status !== 401) return 'authFailureGeneric'
  // Both steps are named, so neither is the "everything else" branch: a step the SPA did not name
  // gets the generic failure, and a mistyped step cannot quietly read as a credential rejection.
  switch (step) {
    case 'credentials':
      return 'invalidCredentials'
    case 'verify':
      if (code === OTP_REJECTED_CODE) return 'otpFailed'
      if (code === AUTH_REJECTED_CODE) return 'verifyAuthFailed'
      return 'authFailureGeneric'
    default:
      return 'authFailureGeneric'
  }
}
